import React, { useState } from "react";
import {
  Box,
  Typography,
  Grid,
  MenuItem,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  TextField,
  InputAdornment,
} from "@mui/material";
import FormTextField from "../../../../../../../core/ui/FormTextField";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { BirthDatePickerIcon } from "../../../../../../../core/global/Icons";

const REGULATORY_STATUS_OPTIONS = ["Independent Regulator", "Non-Independent Regulator", "Unregulated"];

const SectionBox = ({ title, children }) => (
  <Box
    sx={{
      bgcolor: "#fff",
      borderRadius: "12px",
      p: 3,
      boxShadow: "0px 2px 4px rgba(0,0,0,0.08), 0px 0px 6px rgba(0,0,0,0.02)",
    }}
  >
    {title && <Typography sx={{ fontSize: "16px", fontWeight: 600, color: "#292C2F", mb: 2 }}>{title}</Typography>}
    {children}
  </Box>
);

export default function IncaseOfOthersStep({
  form,
  handleChange,
  errors = {},
}) {
  const [openDatePickers, setOpenDatePickers] = useState({});

  const setDatePickerOpen = (key, value) => {
    setOpenDatePickers((prev) => ({ ...prev, [key]: value }));
  };

  const actingOnBehalf = form.actingOnBehalf || "No";
  const isPEP = form.isPEP || "No";

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {/* Basic Details */}
      <SectionBox title="Incase of Others">
        <Grid container sx={{ gap: "24px" }}>
          <Grid item xs={12} sm="auto">
            <FormTextField
              label="City & Country of Incorporation"
              value={form.cityCountryIncorporation || ""}
              onChange={handleChange("cityCountryIncorporation")}
              placeholder="Taguig City, Philippines"
              maxwidth="361px"
            />
          </Grid>
          <Grid item xs={12} sm="auto">
            <FormTextField
              label="Principal business/activity"
              value={form.principalBusiness || ""}
              onChange={handleChange("principalBusiness")}
              placeholder="Freightforwarding and Logistics"
              maxwidth="361px"
            />
          </Grid>
          <Grid item xs={12} sm="auto">
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: 500,
                color: "#313131",
                mb: 1,
              }}
            >
              Date of Birth/Incorporations
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                open={!!openDatePickers.dateOfIncorporation}
                value={
                  form.dateOfIncorporation
                    ? dayjs(form.dateOfIncorporation)
                    : null
                }
                onChange={(newValue) => {
                  handleChange("dateOfIncorporation")({
                    target: {
                      value: newValue ? newValue.format("YYYY-MM-DD") : "",
                    },
                  });
                  setDatePickerOpen("dateOfIncorporation", false);
                }}
                onClose={() => setDatePickerOpen("dateOfIncorporation", false)}
                inputFormat="MMM. D, YYYY"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    placeholder="Jan. 10, 2026"
                    onClick={() => setDatePickerOpen("dateOfIncorporation", true)}
                    sx={{
                      width: "361px",
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        fontSize: 14,
                        borderWidth: "1px",
                        "& fieldset": { borderColor: "#D6D9DB" },
                        "&:hover fieldset": { borderColor: "#11783C" },
                        "&.Mui-focused fieldset": { borderColor: "#11783C", borderWidth: "1px", },
                      },
                    }}
                    inputProps={{ ...params.inputProps, readOnly: true }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <InputAdornment
                          position="end"
                          sx={{ cursor: "pointer" }}
                          onClick={() =>
                            setDatePickerOpen("dateOfIncorporation", true)
                          }
                        >
                          <BirthDatePickerIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />
            </LocalizationProvider>
          </Grid>
        </Grid>
      </SectionBox>

      {/* Acting on behalf */}
      <SectionBox title="Is the Customer acting on behalf of another Person">
        <FormControl component="fieldset">
          <RadioGroup
            row
            value={actingOnBehalf}
            onChange={handleChange("actingOnBehalf")}
          >
            {["Yes", "No"].map((opt) => (
              <FormControlLabel
                key={opt}
                value={opt}
                control={
                  <Radio
                    size="small"
                    sx={{
                      color: "#003F2E",
                      "&.Mui-checked": { color: "#003F2E" },
                    }}
                  />
                }
                label={<Typography sx={{ fontSize: 14 }}>{opt}</Typography>}
              />
            ))}
          </RadioGroup>
        </FormControl>

        {actingOnBehalf === "Yes" && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
            <Grid container sx={{ gap: "24px" }}>
              <Grid item xs={12} sm="auto">
                <FormTextField
                  label="Name of the beneficial owner"
                  value={form.beneficialOwnerName || ""}
                  onChange={handleChange("beneficialOwnerName")}
                  placeholder=""
                  maxwidth="220px"
                />
              </Grid>
              <Grid item xs={12} sm="auto">
                <FormTextField
                  label="ID No./CR No./Registration No."
                  value={form.beneficialOwnerID || ""}
                  onChange={handleChange("beneficialOwnerID")}
                  placeholder=""
                  maxwidth="220px"
                />
              </Grid>
              <Grid item xs={12} sm="auto">
                <FormTextField
                  label="Domicile"
                  value={form.domicile || ""}
                  onChange={handleChange("domicile")}
                  placeholder=""
                  maxwidth="220px"
                />
              </Grid>
            </Grid>

            <Typography sx={{ fontSize: 13, color: "#5B6167", mt: 1 }}>
              If Beneficial Owner is an Individual, then:
            </Typography>
            <FormTextField
              label="Nationality"
              value={form.nationality || ""}
              onChange={handleChange("nationality")}
              placeholder=""
              maxwidth="320px"
            />

            <Box>
              <Typography
                sx={{ fontSize: 14, fontWeight: 600, color: "#313131", mb: 1 }}
              >
                Is the individual a Politically Important Person (PEP)?
              </Typography>
              <FormControl component="fieldset">
                <RadioGroup row value={isPEP} onChange={handleChange("isPEP")}>
                  {["Yes", "No"].map((opt) => (
                    <FormControlLabel
                      key={opt}
                      value={opt}
                      control={
                        <Radio
                          size="small"
                          sx={{
                            color: "#003F2E",
                            "&.Mui-checked": { color: "#003F2E" },
                          }}
                        />
                      }
                      label={
                        <Typography sx={{ fontSize: 14 }}>{opt}</Typography>
                      }
                    />
                  ))}
                </RadioGroup>
              </FormControl>
            </Box>
          </Box>
        )}
      </SectionBox>

      {/* Regulatory */}
      <SectionBox>
        <Grid container sx={{ gap: "24px" }}>
          <Grid item xs={12} sm="auto">
            <FormTextField
              label="Regulatory Status"
              value={form.regulatoryStatus || ""}
              onChange={handleChange("regulatoryStatus")}
              select
              maxwidth="320px"
              SelectProps={{
                displayEmpty: true,
                renderValue: (v) =>
                  v || <span style={{ color: "#999" }}>Select</span>,
              }}
            >
              {REGULATORY_STATUS_OPTIONS.map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              ))}
            </FormTextField>
          </Grid>
          <Grid item xs={12} sm="auto">
            <FormTextField
              label="Name of Regulatory, if any"
              value={form.nameOfRegulatory || ""}
              onChange={handleChange("nameOfRegulatory")}
              placeholder=""
              maxwidth="320px"
            />
          </Grid>
          <Grid item xs={12} sm="auto">
            <FormTextField
              label="Name of Stock Exchange"
              value={form.nameOfStockExchange || ""}
              onChange={handleChange("nameOfStockExchange")}
              placeholder=""
              maxwidth="320px"
            />
          </Grid>
        </Grid>

        <Grid container sx={{ gap: "24px", mt: 2 }}>
          <Grid item xs={12} sm="auto">
            <FormTextField
              label="If a holding company, name of any other subsidiary/branches/associated"
              value={form.subsidiaryBranches || ""}
              onChange={handleChange("subsidiaryBranches")}
              placeholder=""
              maxwidth="361px"
            />
          </Grid>
          <Grid item xs={12} sm="auto">
            <FormTextField
              label="If not, Group company (If Any)"
              value={form.groupCompany || ""}
              onChange={handleChange("groupCompany")}
              placeholder=""
              maxwidth="361px"
            />
          </Grid>
        </Grid>

        <Box sx={{ mt: 2 }}>
          <FormTextField
            label="If business activities are conducted in more than one country, please indicate names of all countries"
            value={form.businessCountries || ""}
            onChange={handleChange("businessCountries")}
            placeholder=""
          />
        </Box>
      </SectionBox>

      {/* Contact Details */}
      <SectionBox title="Contact Details of Customer">
        <Grid container sx={{ gap: "24px" }}>
          <Grid item xs={12} sm="auto">
            <FormTextField
              label="Contact Person"
              value={form.contactPerson || ""}
              onChange={handleChange("contactPerson")}
              placeholder="(ContactPerson From Leads)"
              maxwidth="361px"
            />
          </Grid>
          <Grid item xs={12} sm="auto">
            <FormTextField
              label="Contact detail of compliance officer, If any"
              value={form.complianceOfficer || ""}
              onChange={handleChange("complianceOfficer")}
              placeholder=""
              maxwidth="361px"
            />
          </Grid>
        </Grid>
        <Grid container sx={{ gap: "24px", mt: "24px" }}>
          <Grid item xs={12} sm="auto">
            <FormTextField
              label="Telephone"
              value={form.telephone || ""}
              onChange={handleChange("telephone")}
              placeholder="(Telephone From Leads)"
              maxwidth="220px"
            />
          </Grid>
          <Grid item xs={12} sm="auto">
            <FormTextField
              label="Mobile"
              value={form.mobile || ""}
              onChange={handleChange("mobile")}
              placeholder="(Mobile From Leads)"
              maxwidth="220px"
            />
          </Grid>
          <Grid item xs={12} sm="auto">
            <FormTextField
              label="Email/Website"
              value={form.emailWebsite || ""}
              onChange={handleChange("emailWebsite")}
              placeholder="(Email/Website From Leads)"
              maxwidth="220px"
            />
          </Grid>
        </Grid>
      </SectionBox>
    </Box>
  );
}
